import React, { useEffect, useState } from 'react';
import { useProjectStore, SyncStatus } from '../../stores/projectStore';
import { formatDateTime } from '../../utils/formatDateTime';
import ConfirmDialog from '../ui/ConfirmDialog';

interface SyncStatusIndicatorProps {
    className?: string;
}

function SyncStatusIndicator({ className = '' }: SyncStatusIndicatorProps) {
    const {
        activeProject,
        pendingChanges,
        syncStatus,
        syncError,
        lastSyncedAt,
        saveWorkspaceNow,
        loadProject,
    } = useProjectStore();
    const [confirmReloadOpen, setConfirmReloadOpen] = useState(false);
    const [reloadSubmitting, setReloadSubmitting] = useState(false);
    const isVersionConflict = syncError?.startsWith('Version conflict') ?? false;
    const visibleError = isVersionConflict ? 'Version conflict' : (syncError || 'Save failed');

    const handleRecovery = () => {
        if (!activeProject) return;
        if (isVersionConflict) {
            setConfirmReloadOpen(true);
            return;
        }
        void saveWorkspaceNow(activeProject.user_id);
    };

    const handleConfirmReload = async () => {
        if (!activeProject) return;
        setReloadSubmitting(true);
        try {
            const reloaded = await loadProject(activeProject.project_id, activeProject.user_id, {
                preserveViewport: true,
                discardPendingChanges: true,
            });
            if (reloaded) {
                setConfirmReloadOpen(false);
            }
        } finally {
            setReloadSubmitting(false);
        }
    };

    useEffect(() => {
        if (!pendingChanges) return;

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [pendingChanges]);

    const getStatusConfig = (status: SyncStatus) => {
        switch (status) {
            case 'idle':
                return {
                    icon: null,
                    text: '',
                    color: 'text-fg-tertiary',
                    bgColor: 'bg-bg-warm',
                };
            case 'pending':
                return {
                    icon: (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    ),
                    text: 'Unsaved changes',
                    color: 'text-warning',
                    bgColor: 'bg-warning-bg',
                };
            case 'syncing':
                return {
                    icon: (
                        <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    ),
                    text: 'Saving...',
                    color: 'text-accent',
                    bgColor: 'bg-accent-tint',
                };
            case 'synced':
                return {
                    icon: (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    ),
                    text: 'Saved',
                    color: 'text-success',
                    bgColor: 'bg-success-bg',
                };
            case 'error':
                return {
                    icon: (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    ),
                    text: visibleError,
                    color: 'text-danger',
                    bgColor: 'bg-danger-bg',
                };
            default:
                return {
                    icon: null,
                    text: '',
                    color: 'text-fg-tertiary',
                    bgColor: 'bg-bg-warm',
                };
        }
    };

    const config = getStatusConfig(syncStatus);

    // Don't show anything in idle state
    if (syncStatus === 'idle') {
        return null;
    }

    return (
        <>
            <div
                className={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs ${config.color} ${config.bgColor} ${className}`}
                role={syncStatus === 'error' ? 'alert' : 'status'}
                aria-live={syncStatus === 'error' ? 'assertive' : 'polite'}
                title={lastSyncedAt
                    ? `Last saved at ${formatDateTime(lastSyncedAt, { context: 'time' })}`
                    : ''}
            >
                {config.icon}
                <span>{config.text}</span>
                {syncStatus === 'synced' && lastSyncedAt && (
                    <span className="text-success ml-1">
                        {formatDateTime(lastSyncedAt, { context: 'time' })}
                    </span>
                )}
                {syncStatus === 'error' && (
                    <button
                        type="button"
                        onClick={handleRecovery}
                        disabled={!activeProject}
                        aria-label={isVersionConflict ? 'Reload project' : 'Retry save'}
                        className="ml-1 rounded-md border border-danger/40 bg-surface px-2 py-0.5 font-medium text-danger transition-colors hover:bg-danger-bg focus-visible:outline-none focus-visible:shadow-bark-focus disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {isVersionConflict ? 'Reload project' : 'Retry'}
                    </button>
                )}
            </div>
            <ConfirmDialog
                open={confirmReloadOpen}
                onCancel={() => setConfirmReloadOpen(false)}
                onConfirm={handleConfirmReload}
                title="Discard unsaved changes?"
                description="Reloading discards the unsaved changes in this tab and opens the server's newer version."
                error={confirmReloadOpen && !isVersionConflict ? syncError : null}
                confirmLabel="Discard and reload"
                variant="destructive"
                submitting={reloadSubmitting}
            />
        </>
    );
}

export default SyncStatusIndicator;
